import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Button from "@amzn/awsui-components-react/polaris/button";
import Icon from "@amzn/awsui-components-react/polaris/icon";

import { RootState } from "../app/rootReducer";
import { isNoFilterApplied } from "../searchSideBar/queryStringHelpers";
import { trackButtonClick } from "../telemetry/telemetry";
import {
  isOnboardingDismissed,
  markOnboardingDismissed,
} from "./onboardingPreference";

/**
 * First-visit hint for the App Shell layout. Addresses the top switch
 * reasons from the Sep 2026 feedback review ("can't find what I need",
 * "layout is confusing"): users didn't realise the property filter
 * accepts plain typing and narrows every property/value as they go.
 *
 * Shows once per browser. Hidden for good when the user dismisses it
 * or applies any filter (see `onboardingPreference.ts`). Mounted in
 * `AppShell`'s notifications container so it sits with the other
 * banners above the routed content.
 */

// FilterBar's row wrapper. Focusing its `<input>` opens Cloudscape's
// property-filter suggestions, which is the whole point of the CTA.
const FILTER_BAR_INPUT_SELECTOR = '[data-testid="filter-bar"] input';

export const NewViewOnboardingAlert: React.FC = () => {
  const [visible, setVisible] = useState<boolean>(
    () => !isOnboardingDismissed()
  );
  const { queryString } = useSelector(
    (state: RootState) => state.generateQuery
  );

  const dismiss = useCallback((reason: "dismiss" | "filterApplied") => {
    markOnboardingDismissed();
    setVisible(false);
    trackButtonClick("appShell", "onboardingDismiss", { reason });
  }, []);

  // Option (b): once a filter is applied the hint has done its job.
  useEffect(() => {
    if (visible && !isNoFilterApplied(queryString)) {
      dismiss("filterApplied");
    }
  }, [visible, queryString, dismiss]);

  const onOpenFilters = useCallback(() => {
    trackButtonClick("appShell", "onboardingOpenFilters");
    const input = document.querySelector<HTMLInputElement>(
      FILTER_BAR_INPUT_SELECTOR
    );
    input?.focus();
  }, []);

  if (!visible) {
    return null;
  }

  return (
    <Alert
      data-testid="new-view-onboarding-alert"
      type="info"
      dismissible
      dismissAriaLabel="Dismiss"
      onDismiss={() => dismiss("dismiss")}
      header="Search is simpler than it looks"
      action={
        <Button
          data-testid="new-view-onboarding-open-filters"
          iconName="search"
          onClick={onOpenFilters}
        >
          Open filters
        </Button>
      }
    >
      Click the filter bar (<Icon name="search" size="small" />) above and just
      start typing what you&apos;re looking for &mdash; an ASIN, a program name,
      a marketplace, a status &mdash; and the list narrows to the matching
      filters and values as you type. Pick one or more filters and then press{" "}
      <b>Submit</b>. You can also choose a <b>Saved filter set</b> for a
      one-click view.
    </Alert>
  );
};

export default NewViewOnboardingAlert;
